/**
 * AfaqBooks Online — 8-tab accounting dashboard
 * Tabs: Dashboard | Invoices | Expenses | Banking | Reports | VAT & FTA | Chart of Accounts | Payroll
 */

import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../services/api";

// ── CSS ──────────────────────────────────────────────────────────────────────
const STYLES = `
.ab-root {
  color: var(--color-on-surface, #dae2fd);
  font-family: 'Space Grotesk', sans-serif;
  padding: 0 0 60px 0;
}
.ab-topbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 32px 12px;
  border-bottom: 1px solid var(--border-ghost, rgba(255,255,255,0.06));
}
.ab-topbar h1 {
  font-family: 'Manrope', sans-serif;
  font-size: 1.5rem; font-weight: 700;
  color: var(--color-primary, #ffd6a5);
  margin: 0;
}
.ab-badge { font-size: .7rem; background: #ffd6a520; color: #ffd6a5; border-radius: 6px; padding: 2px 8px; margin-left: 8px; }
.ab-tabs {
  display: flex; gap: 4px; padding: 16px 32px 0;
  border-bottom: 1px solid var(--border-ghost, rgba(255,255,255,0.06));
  overflow-x: auto;
}
.ab-tab {
  padding: 8px 18px; border: none; border-radius: 8px 8px 0 0;
  background: transparent; color: var(--color-on-surface, #dae2fd);
  cursor: pointer; font-size: .88rem; white-space: nowrap;
  transition: background .15s;
}
.ab-tab:hover { background: rgba(255,255,255,.06); }
.ab-tab.active {
  background: var(--glass-bg, rgba(19,27,46,0.72));
  color: var(--color-primary, #ffd6a5);
  border-bottom: 2px solid var(--color-primary, #ffd6a5);
}
.ab-content { padding: 28px 32px; }
.ab-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; margin-bottom: 28px; }
.ab-card {
  background: var(--glass-bg, rgba(19,27,46,0.72));
  border: 1px solid var(--border-ghost, rgba(255,255,255,0.06));
  border-radius: 14px; padding: 20px 22px;
  backdrop-filter: blur(20px);
  transition: transform 0.2s, box-shadow 0.2s;
}
.ab-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}
.ab-card-label { font-size: .75rem; color: #8892b0; margin-bottom: 6px; }
.ab-card-value { font-size: 1.5rem; font-weight: 700; color: var(--color-primary, #ffd6a5); }
.ab-card-sub { font-size: .72rem; color: #8892b0; margin-top: 4px; }
.ab-card.danger .ab-card-value { color: #ff6b6b; }
.ab-card.success .ab-card-value { color: var(--color-tertiary, #b8d58d); }
.ab-section-title {
  font-family: 'Manrope', sans-serif; font-size: 1rem; font-weight: 600;
  color: var(--color-primary, #ffd6a5); margin: 0 0 14px;
}
.ab-table-wrap { overflow-x: auto; }
table.ab-table { width: 100%; border-collapse: collapse; font-size: .85rem; }
.ab-table th { text-align: left; padding: 10px 14px; color: #8892b0; font-weight: 500; border-bottom: 1px solid rgba(255,255,255,.06); }
.ab-table td { padding: 11px 14px; border-bottom: 1px solid rgba(255,255,255,.04); vertical-align: middle; }
.ab-table tr:hover td { background: rgba(255,255,255,.02); }
.ab-badge-status {
  display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: .72rem; font-weight: 600;
}
.s-draft   { background:#334;   color:#aac; }
.s-sent    { background:#1a2a4a; color:#6ab0ff; }
.s-partial { background:#2a2010; color:#f0b040; }
.s-paid    { background:#1a3020; color: var(--color-tertiary, #b8d58d); }
.s-overdue { background:#3a1010; color:#ff6b6b; }
.s-void    { background:#222;   color:#666; }
.s-pending  { background:#2a2010; color:#f0b040; }
.s-approved { background:#1a3020; color:#b8d58d; }
.s-rejected { background:#3a1010; color:#ff6b6b; }
.s-matched  { background:#1a3020; color:#b8d58d; }
.s-unmatched{ background:#2a2010; color:#f0b040; }
.s-excluded { background:#222;   color:#666; }
.ab-btn {
  padding: 8px 18px; border-radius: 8px; border: none; cursor: pointer;
  font-size: .85rem; font-weight: 600; transition: opacity .15s;
}
.ab-btn:hover { opacity: .85; }
.ab-btn:disabled {
  opacity: .5; cursor: not-allowed;
}
.ab-btn-primary { background: var(--color-primary, #ffd6a5); color: #0b1326; }
.ab-btn-ghost   { background: rgba(255,255,255,.07); color: var(--color-on-surface, #dae2fd); }
.ab-btn-danger  { background: #ff6b6b22; color: #ff6b6b; }
.ab-btn-sm { padding: 5px 12px; font-size: .78rem; }
.ab-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 18px; flex-wrap: wrap; }
.ab-input {
  background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.1);
  border-radius: 8px; color: var(--color-on-surface, #dae2fd);
  padding: 8px 12px; font-size: .85rem; outline: none;
}
.ab-input:focus { border-color: var(--color-primary, #ffd6a5); }
.ab-select {
  background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.1);
  border-radius: 8px; color: var(--color-on-surface, #dae2fd);
  padding: 8px 12px; font-size: .85rem;
}
.ab-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.7); z-index: 1000;
  display: flex; align-items: center; justify-content: center;
  backdrop-filter: blur(4px);
}
.ab-modal {
  background: #131b2e; border: 1px solid rgba(255,255,255,.1);
  border-radius: 16px; padding: 28px 32px; width: 90%; max-width: 780px;
  max-height: 90vh; overflow-y: auto; z-index: 1001;
  box-shadow: 0 8px 32px rgba(0,0,0,0.4);
}
.ab-modal h2 { font-family: 'Manrope',sans-serif; color: var(--color-primary,#ffd6a5); margin: 0 0 20px; font-size: 1.15rem; }
.ab-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.ab-form-group { display: flex; flex-direction: column; gap: 6px; }
.ab-form-group label { font-size: .78rem; color: #8892b0; }
.ab-form-group.span2 { grid-column: span 2; }
.ab-line-table { width: 100%; font-size: .82rem; margin-top: 10px; border-collapse: collapse; }
.ab-line-table th { text-align: left; padding: 6px 8px; color: #8892b0; font-size: .75rem; }
.ab-line-table td { padding: 4px 4px; }
.ab-line-table input { width: 100%; }
.ab-totals { text-align: right; margin-top: 14px; line-height: 1.8; }
.ab-totals strong { color: var(--color-primary, #ffd6a5); }
.ab-modal-footer { display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px; }
.ab-notice { font-size: .78rem; color: #8892b0; margin-top: 4px; }
.ab-notice.warn { color: #f0b040; }
.ab-notice.ok   { color: #b8d58d; }
.ab-subtabs { display: flex; gap: 6px; margin-bottom: 20px; flex-wrap: wrap; }
.ab-subtab { padding: 6px 14px; border-radius: 20px; border: 1px solid rgba(255,255,255,.1); background: transparent; color: #8892b0; cursor: pointer; font-size: .82rem; }
.ab-subtab.active { background: rgba(255,214,165,.1); color: var(--color-primary,#ffd6a5); border-color: var(--color-primary,#ffd6a5); }
.ab-coa-group { margin-bottom: 20px; }
.ab-coa-type { font-size: .72rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #8892b0; margin-bottom: 8px; }
.ab-coa-row { display: flex; align-items: center; gap: 12px; padding: 9px 14px; border-radius: 8px; border: 1px solid rgba(255,255,255,.04); margin-bottom: 4px; font-size: .85rem; }
.ab-coa-row:hover { background: rgba(255,255,255,.03); }
.ab-coa-code { width: 60px; color: #8892b0; font-family: monospace; }
.ab-coa-name { flex: 1; }
.ab-coa-balance { width: 120px; text-align: right; font-family: monospace; }
.ab-payroll-row { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr; gap: 10px; align-items: center; padding: 10px 14px; border-radius: 8px; border: 1px solid rgba(255,255,255,.04); margin-bottom: 6px; font-size: .84rem; }

/* ── Import engine ─────────────────────────────────────────────────────── */
.ab-import-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 18px; margin-bottom: 28px; }
.ab-import-card {
  background: rgba(255,255,255,0.02);
  backdrop-filter: blur(60px);
  border: 0.5px solid rgba(189,178,255,0.15);
  border-radius: 16px; padding: 22px;
}
.ab-import-card h3 { font-family: 'Manrope', sans-serif; color: #bdb2ff; margin: 0 0 14px; font-size: .95rem; }
.ab-drop {
  border: 1.5px dashed rgba(189,178,255,0.15);
  border-radius: 14px; padding: 28px 16px;
  text-align: center; color: #6d758b; font-size: .85rem;
  cursor: pointer; transition: border-color .15s, background .15s;
  margin-bottom: 12px;
}
.ab-drop:hover, .ab-drop.active { border-color: #bdb2ff; background: rgba(189,178,255,0.05); color: #dde5ff; }
.ab-drop input { display: none; }
.ab-drop .ab-drop-name { display: block; margin-top: 8px; color: #cfeda2; font-weight: 600; font-size: .82rem; }
.ab-imp-row { display: flex; gap: 8px; align-items: center; margin-bottom: 10px; }
.ab-imp-row label { font-size: .75rem; color: #6d758b; min-width: 72px; }
.ab-imp-result {
  margin-top: 12px; padding: 12px 14px; border-radius: 10px;
  background: rgba(207,237,162,0.08); border: 0.5px solid rgba(207,237,162,0.25);
  font-size: .82rem; color: #dde5ff;
}
.ab-imp-result.err { background: rgba(255,110,132,0.08); border-color: rgba(255,110,132,0.3); color: #ff6e84; }
.ab-imp-field { display: grid; grid-template-columns: 110px 1fr; gap: 8px; margin-bottom: 6px; font-size: .8rem; }
.ab-imp-field span { color: #6d758b; }
.ab-imp-preview { max-height: 140px; overflow: auto; font-family: monospace; font-size: .72rem; color: #dde5ff; background: rgba(0,0,0,.2); padding: 8px; border-radius: 6px; margin-bottom: 10px; }
.ab-imp-preview table { width: 100%; border-collapse: collapse; }
.ab-imp-preview th, .ab-imp-preview td { padding: 3px 6px; border-bottom: 1px solid rgba(255,255,255,.05); text-align: left; }
.ab-imp-preview th { color: #bdb2ff; font-weight: 700; }
.ab-imp-confidence { display: inline-block; padding: 2px 8px; border-radius: 12px; background: rgba(189,178,255,0.12); color: #bdb2ff; font-size: .72rem; font-weight: 600; }
@media print {
  .ab-root { padding: 0; background: white; color: black; }
  .ab-tabs, .ab-topbar, .ab-toolbar, .afaq-sidebar, .afaq-topnav, .ab-no-print { display: none !important; }
  .ab-card { border: 1px solid #ddd; background: white; color: black; }
  .ab-table th, .ab-table td { color: black; }
}
`;

// ── types ─────────────────────────────────────────────────────────────────────
type Tab = "dashboard" | "invoices" | "expenses" | "banking" | "reports" | "vat" | "coa" | "payroll" | "import" | "online-pl" | "shopify-pl";
type ReportTab = "pl" | "bs" | "tb" | "ar" | "cf";

interface DashboardData {
  revenue_month?: number;
  expenses_month?: number;
  net_profit_month?: number;
  outstanding_count?: number;
  outstanding_amount?: number;
  overdue_amount?: number;
  bank_balance?: number;
  error?: string;
}
interface InvoiceItem { 
  id: string; 
  invoice_number: string; 
  client_name: string; 
  total: number; 
  issue_date: string; 
  due_date: string; 
  status: string; 
  balance_due: number; 
}
interface ExpenseItem { 
  id: string; 
  date: string; 
  vendor: string; 
  category: string; 
  amount: number; 
  vat_amount: number; 
  total: number; 
  status: string; 
}
interface BankTx { 
  id: string; 
  date: string; 
  description: string; 
  amount: number; 
  status: string; 
  suggested_category: string; 
  reference: string; 
}
interface AccountItem { 
  id: string; 
  code: string; 
  name: string; 
  type: string; 
  balance: number; 
  is_active: boolean; 
  is_bank?: boolean; 
}
interface LineItem { 
  description: string; 
  quantity: number; 
  unit_price: number; 
  vat_rate: number; 
}

// ── helpers ───────────────────────────────────────────────────────────────────
const fmt = (n: number | undefined) => {
  const num = n ?? 0;
  return `AED ${num.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const statusBadge = (s: string) => {
  const statusClass = s.toLowerCase().replace(/\s+/g, '-');
  return <span className={`ab-badge-status s-${statusClass}`}>{s}</span>;
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const CATEGORIES = [
  "Rent", "Utilities", "Salaries", "Marketing", "Office Supplies", 
  "Travel", "Repairs", "IT / Software", "Insurance", "Professional Fees", "Other"
];

function StatCard({ 
  label, 
  value, 
  sub, 
  variant 
}: { 
  label: string; 
  value: string; 
  sub?: string; 
  variant?: "danger" | "success" 
}) {
  return (
    <div className={`ab-card${variant ? " " + variant : ""}`}>
      <div className="ab-card-label">{label}</div>
      <div className="ab-card-value">{value}</div>
      {sub && <div className="ab-card-sub">{sub}</div>}
    </div>
  );
}

// ── Loading Spinner ───────────────────────────────────────────────────────────
function LoadingSpinner() {
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      padding: '40px 0',
      color: '#8892b0'
    }}>
      <div style={{
        width: '24px',
        height: '24px',
        border: '2px solid rgba(255,255,255,0.1)',
        borderTop: '2px solid var(--color-primary, #ffd6a5)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      <span style={{ marginLeft: '12px' }}>Loading...</span>
    </div>
  );
}

// ── Error Message ─────────────────────────────────────────────────────────────
function ErrorMessage({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div style={{
      background: 'rgba(255,107,107,0.1)',
      border: '1px solid rgba(255,107,107,0.3)',
      borderRadius: '8px',
      padding: '16px',
      margin: '16px 0',
      color: '#ff6b6b',
      fontSize: '0.85rem'
    }}>
      <div style={{ fontWeight: 600, marginBottom: '8px' }}>Error</div>
      <div>{message}</div>
      {onRetry && (
        <button 
          className="ab-btn ab-btn-ghost ab-btn-sm" 
          onClick={onRetry}
          style={{ marginTop: '12px' }}
        >
          Try Again
        </button>
      )}
    </div>
  );
}

// ── Confirmation Dialog ───────────────────────────────────────────────────────
function ConfirmDialog({ 
  title, 
  message, 
  onConfirm, 
  onCancel 
}: { 
  title: string; 
  message: string; 
  onConfirm: () => void; 
  onCancel: () => void 
}) {
  return (
    <div className="ab-overlay" onClick={onCancel}>
      <div className="ab-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
        <h2>{title}</h2>
        <p style={{ marginBottom: '20px', lineHeight: 1.6 }}>{message}</p>
        <div className="ab-modal-footer">
          <button className="ab-btn ab-btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="ab-btn ab-btn-primary" onClick={onConfirm}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function AccountingDashboard() {
  const [tab, setTab] = useState<Tab>("dashboard");
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<Record<string, string>>({});

  // Dashboard
  const [dash, setDash] = useState<DashboardData>({});

  // Online P&L
  const [oplMonth, setOplMonth] = useState<string>(currentMonth());
  const [oplData, setOplData] = useState<any>(null);

  // Invoices
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [invFilter, setInvFilter] = useState("");
  const [showNewInv, setShowNewInv] = useState(false);
  const [invForm, setInvForm] = useState({
    client_name: "", client_email: "", client_trn: "",
    issue_date: todayStr(), due_date: todayStr(),
    notes: "", payment_terms: "Net 30"
  });
  const [invLines, setInvLines] = useState<LineItem[]>([
    { description: "", quantity: 1, unit_price: 0, vat_rate: 5 }
  ]);
  const [trnStatus, setTrnStatus] = useState<"" | "ok" | "warn">("");

  // Expenses
  const [expenses, setExpenses] = useState<ExpenseItem[]>([]);
  const [expFilter, setExpFilter] = useState("");
  const [showNewExp, setShowNewExp] = useState(false);
  const [expForm, setExpForm] = useState({
    date: todayStr(), vendor: "", vendor_trn: "",
    category: "", amount: "", vat_amount: "0",
    notes: "", is_recurring: false, recur_interval: "monthly"
  });

  // Banking
  const [bankTxs, setBankTxs] = useState<BankTx[]>([]);
  const [bankFilter, setBankFilter] = useState("unmatched");
  const [bankAccounts, setBankAccounts] = useState<AccountItem[]>([]);
  const [selectedBankAcct, setSelectedBankAcct] = useState("");

  // Reports
  const [reportTab, setReportTab] = useState<ReportTab>("pl");
  const [reportFrom, setReportFrom] = useState(firstOfMonth());
  const [reportTo, setReportTo] = useState(todayStr());
  const [reportData, setReportData] = useState<any>(null);

  // VAT
  const [vatSummary, setVatSummary] = useState<any>(null);
  const [trnInput, setTrnInput] = useState("");
  const [trnResult, setTrnResult] = useState<any>(null);
  const [vatPeriodFrom, setVatPeriodFrom] = useState(firstOfMonth());
  const [vatPeriodTo, setVatPeriodTo] = useState(todayStr());
  const [vatReturn, setVatReturn] = useState<any>(null);

  // CoA
  const [accounts, setAccounts] = useState<AccountItem[]>([]);
  const [setupDone, setSetupDone] = useState(false);

  // Payroll
  const [payrollMonth, setPayrollMonth] = useState(currentMonth());
  const [payrollResult, setPayrollResult] = useState<any[]>([]);

  // Import Engine
  const [impCsvCategory, setImpCsvCategory] = useState("Google Ads");
  const [impCsvFile, setImpCsvFile] = useState<File | null>(null);
  const [impCsvPreview, setImpCsvPreview] = useState<string[][]>([]);
  const [impCsvResult, setImpCsvResult] = useState<any>(null);

  const [impPdfCategory, setImpPdfCategory] = useState("Imported Invoices");
  const [impPdfFile, setImpPdfFile] = useState<File | null>(null);
  const [impPdfResult, setImpPdfResult] = useState<any>(null);

  const [impImgCategory, setImpImgCategory] = useState("Imported Receipts");
  const [impImgFile, setImpImgFile] = useState<File | null>(null);
  const [impImgResult, setImpImgResult] = useState<any>(null);

  const [impHistory, setImpHistory] = useState<any[]>([]);

  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ show: false, title: "", message: "", onConfirm: () => {} });

  // ── API helpers ─────────────────────────────────────────────────────────
  const acctApi = (api.accounting as any);

  const setLoadingState = (key: string, isLoading: boolean) => {
    setLoading(prev => ({ ...prev, [key]: isLoading }));
  };

  const setErrorState = (key: string, errorMessage: string) => {
    setError(prev => ({ ...prev, [key]: errorMessage }));
  };

  const clearErrorState = (key: string) => {
    setError(prev => {
      const newErrors = { ...prev };
      delete newErrors[key];
      return newErrors;
    });
  };

  // ── loaders ───────────────────────────────────────────────────────────────
  const loadDash = useCallback(async () => {
    setLoadingState('dashboard', true);
    clearErrorState('dashboard');
    try { 
      setDash(await acctApi.dashboard()); 
    } catch (err: any) {
      setErrorState('dashboard', err.message || 'Failed to load dashboard');
    } finally {
      setLoadingState('dashboard', false);
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    setLoadingState('invoices', true);
    clearErrorState('invoices');
    try { 
      const d = await acctApi.invoices.list(); 
      setInvoices(d.invoices ?? d); 
    } catch (err: any) {
      setErrorState('invoices', err.message || 'Failed to load invoices');
    } finally {
      setLoadingState('invoices', false);
    }
  }, []);

  const loadExpenses = useCallback(async () => {
    setLoadingState('expenses', true);
    clearErrorState('expenses');
    try { 
      const d = await acctApi.expenses.list(); 
      setExpenses(d.expenses ?? d); 
    } catch (err: any) {
      setErrorState('expenses', err.message || 'Failed to load expenses');
    } finally {
      setLoadingState('expenses', false);
    }
  }, []);

  const loadBankTxs = useCallback(async () => {
    setLoadingState('banking', true);
    clearErrorState('banking');
    try {
      const params: Record<string, string> = { status: bankFilter };
      if (selectedBankAcct) params.account_id = selectedBankAcct;
      const d = await acctApi.bank.list(params);
      setBankTxs(d.transactions ?? d);
    } catch (err: any) {
      setErrorState('banking', err.message || 'Failed to load bank transactions');
    } finally {
      setLoadingState('banking', false);
    }
  }, [bankFilter, selectedBankAcct]);

  const loadAccounts = useCallback(async () => {
    setLoadingState('coa', true);
    clearErrorState('coa');
    try { 
      const d = await acctApi.accounts.list(); 
      setAccounts(d.accounts ?? d); 
    } catch (err: any) {
      setErrorState('coa', err.message || 'Failed to load accounts');
    } finally {
      setLoadingState('coa', false);
    }
  }, []);

  const loadBankAccounts = useCallback(async () => {
    try { 
      const d = await acctApi.accounts.list(); 
      setBankAccounts((d.accounts ?? d).filter((a: AccountItem) => a.is_bank || a.type === "bank")); 
    } catch (err) {
      console.error('Failed to load bank accounts:', err);
    }
  }, []);

  const loadVatSummary = useCallback(async () => {
    setLoadingState('vat', true);
    clearErrorState('vat');
    try { 
      setVatSummary(await acctApi.vat.summary()); 
    } catch (err: any) {
      setErrorState('vat', err.message || 'Failed to load VAT summary');
    } finally {
      setLoadingState('vat', false);
    }
  }, []);

  const loadOnlinePL = useCallback(async () => {
    setLoadingState('online-pl', true);
    clearErrorState('online-pl');
    try {
      const r = await (acctApi as any).monthlySummary(oplMonth);
      setOplData(r);
    } catch (err: any) {
      setErrorState('online-pl', err.message || 'Failed to load Online P&L');
      setOplData(null);
    } finally {
      setLoadingState('online-pl', false);
    }
  }, [oplMonth]);

  const loadImportHistory = useCallback(async () => {
    try { 
      const d = await acctApi.importHistory?.(); 
      setImpHistory(Array.isArray(d) ? d : (d?.history ?? [])); 
    } catch (err) {
      console.error('Failed to load import history:', err);
    }
  }, []);

  const loadReport = useCallback(async () => {
    setLoadingState('reports', true);
    clearErrorState('reports');
    try {
      let d: any = null;
      if (reportTab === "pl") d = await acctApi.reports.incomeStatement(reportFrom, reportTo);
      if (reportTab === "bs") d = await acctApi.reports.balanceSheet();
      if (reportTab === "tb") d = await acctApi.reports.trialBalance();
      if (reportTab === "ar") d = await acctApi.reports.arAging?.() ?? {};
      if (reportTab === "cf") d = await acctApi.reports.cashFlow?.(reportFrom, reportTo) ?? {};
      setReportData(d);
    } catch (err: any) {
      setErrorState('reports', err.message || 'Failed to load report');
      setReportData(null);
    } finally {
      setLoadingState('reports', false);
    }
  }, [reportTab, reportFrom, reportTo]);

  // ── tab mount effects ─────────────────────────────────────────────────────
  useEffect(() => {
    if (tab === "dashboard") loadDash();
    if (tab === "invoices") loadInvoices();
    if (tab === "expenses") loadExpenses();
    if (tab === "banking") { loadBankTxs(); loadBankAccounts(); }
    if (tab === "coa") loadAccounts();
    if (tab === "vat") loadVatSummary();
    if (tab === "import") loadImportHistory();
    if (tab === "online-pl") loadOnlinePL();
    if (tab === "reports") loadReport();
  }, [tab]);

  useEffect(() => { if (tab === "online-pl") loadOnlinePL(); }, [oplMonth, loadOnlinePL]);
  useEffect(() => { if (tab === "banking") loadBankTxs(); }, [bankFilter, selectedBankAcct, loadBankTxs]);
  useEffect(() => { if (tab === "reports") loadReport(); }, [reportTab, loadReport]);

  // ── invoice computed ──────────────────────────────────────────────────────
  const invSubtotal = invLines.reduce((s, l) => s + l.quantity * l.unit_price, 0);
  const invVat = invLines.reduce((s, l) => s + l.quantity * l.unit_price * l.vat_rate / 100, 0);
  const invTotal = invSubtotal + invVat;

  const updateInvLine = (i: number, field: keyof LineItem, value: any) =>
    setInvLines(ls => ls.map((l, j) => j === i ? { ...l, [field]: value } : l));

  const validateTrn = async (trn: string) => {
    if (!trn || trn.length < 15) { setTrnStatus(""); return; }
    try { 
      const r = await acctApi.fta.validateTrn(trn); 
      setTrnStatus(r.valid ? "ok" : "warn"); 
    } catch {
      setTrnStatus("warn");
    }
  };

  const saveInvoice = async () => {
    if (!invForm.client_name.trim()) {
      alert('Client name is required');
      return;
    }

    setLoadingState('saveInvoice', true);
    try {
      await acctApi.invoices.create({
        ...invForm,
        lines: invLines.map(l => ({
          ...l,
          vat_code: l.vat_rate > 0 ? "SR" : "ZR",
          amount: l.quantity * l.unit_price
        })),
      });
      setShowNewInv(false);
      setInvForm({
        client_name: "", client_email: "", client_trn: "",
        issue_date: todayStr(), due_date: todayStr(),
        notes: "", payment_terms: "Net 30"
      });
      setInvLines([{ description: "", quantity: 1, unit_price: 0, vat_rate: 5 }]);
      setTrnStatus("");
      loadInvoices();
    } catch (err: any) {
      alert(`Failed to save invoice: ${err.message}`);
    } finally {
      setLoadingState('saveInvoice', false);
    }
  };

  // ── expense helpers ───────────────────────────────────────────────────────
  const saveExpense = async () => {
    if (!expForm.vendor.trim()) {
      alert('Vendor is required');
      return;
    }
    if (!expForm.category) {
      alert('Category is required');
      return;
    }

    const amount = parseFloat(expForm.amount) || 0;
    if (amount <= 0) {
      alert('Amount must be positive');
      return;
    }

    setLoadingState('saveExpense', true);
    try {
      const vat = parseFloat(expForm.vat_amount) || 0;
      await acctApi.expenses.create({
        ...expForm,
        amount,
        vat_amount: vat,
        total: amount + vat
      });
      setShowNewExp(false);
      setExpForm({
        date: todayStr(), vendor: "", vendor_trn: "",
        category: "", amount: "", vat_amount: "0",
        notes: "", is_recurring: false, recur_interval: "monthly"
      });
      loadExpenses();
    } catch (err: any) {
      alert(`Failed to save expense: ${err.message}`);
    } finally {
      setLoadingState('saveExpense', false);
    }
  };

  // ── VAT ───────────────────────────────────────────────────────────────────
  const calcVatReturn = async () => {
    setLoadingState('vatCalc', true);
    try { 
      setVatReturn(await acctApi.vat.calculate(vatPeriodFrom, vatPeriodTo)); 
    } catch (err: any) {
      alert(`VAT calculation failed: ${err.message}`);
    } finally {
      setLoadingState('vatCalc', false);
    }
  };

  const submitVatReturn = async () => {
    setConfirmDialog({
      show: true,
      title: "Submit VAT Return",
      message: "Are you sure you want to submit this VAT return to EmaraTax? This action cannot be undone.",
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, show: false }));
        setLoadingState('vatSubmit', true);
        try { 
          await acctApi.vat.submit(vatPeriodFrom, vatPeriodTo); 
          alert("VAT Return submitted to EmaraTax (sandbox)"); 
        } catch (err: any) {
          alert(`VAT submission failed: ${err.message}`);
        } finally {
          setLoadingState('vatSubmit', false);
        }
      }
    });
  };

  const lookupTrn = async () => {
    if (!trnInput.trim()) {
      alert('Please enter a TRN number');
      return;
    }

    setLoadingState('trnLookup', true);
    try { 
      setTrnResult(await acctApi.fta.validateTrn(trnInput)); 
    } catch (err: any) {
      setTrnResult({ valid: false, message: `Lookup failed: ${err.message}` });
    } finally {
      setLoadingState('trnLookup', false);
    }
  };

  // ── payroll ───────────────────────────────────────────────────────────────
  const runPayroll = async () => {
    setLoadingState('payroll', true);
    clearErrorState('payroll');
    try { 
      const d = await acctApi.payroll.calculate({ month: payrollMonth }); 
      setPayrollResult(d.payroll ?? []); 
    } catch (err: any) {
      setErrorState('payroll', err.message || 'Failed to calculate payroll');
    } finally {
      setLoadingState('payroll', false);
    }
  };

  // ── import helpers ────────────────────────────────────────────────────────
  const previewCsv = async (f: File) => {
    try {
      const text = await f.text();
      const lines = text.split(/\r?\n/).filter(Boolean).slice(0, 6);
      setImpCsvPreview(lines.map(l => l.split(",")));
    } catch (err) {
      console.error('CSV preview failed:', err);
      setImpCsvPreview([]);
    }
  };

  const onPickCsv = async (f: File | null) => {
    setImpCsvFile(f);
    setImpCsvResult(null);
    if (f) await previewCsv(f);
    else setImpCsvPreview([]);
  };

  const runCsvImport = async () => {
    if (!impCsvFile) return;
    
    setLoadingState('csvImport', true);
    try {
      const form = new FormData();
      form.append("file", impCsvFile);
      form.append("category", impCsvCategory);
      const r = await acctApi.importCsv?.(form);
      setImpCsvResult(r);
      loadImportHistory();
    } catch (e: any) {
      setImpCsvResult({ error: e?.message || "Import failed" });
    } finally {
      setLoadingState('csvImport', false);
    }
  };

  const runPdfImport = async () => {
    if (!impPdfFile) return;
    
    setLoadingState('pdfImport', true);
    try {
      const form = new FormData();
      form.append("file", impPdfFile);
      form.append("category", impPdfCategory);
      const r = await acctApi.importPdf?.(form);
      setImpPdfResult(r);
      loadImportHistory();
    } catch (e: any) {
      setImpPdfResult({ error: e?.message || "PDF import failed" });
    } finally {
      setLoadingState('pdfImport', false);
    }
  };

  const runImgImport = async () => {
    if (!impImgFile) return;
    
    setLoadingState('imgImport', true);
    try {
      const form = new FormData();
      form.append("file", impImgFile);
      form.append("category", impImgCategory);
      const r = await acctApi.importImage?.(form);
      setImpImgResult(r);
      loadImportHistory();
    } catch (e: any) {
      setImpImgResult({ error: e?.message || "OCR failed" });
    } finally {
      setLoadingState('imgImport', false);
    }
  };

  // ── CoA grouping ──────────────────────────────────────────────────────────
  const coaGroups: Record<string, AccountItem[]> = {};
  accounts.forEach(a => { (coaGroups[a.type] = coaGroups[a.type] || []).push(a); });

  const filteredInv = invoices.filter(i =>
    !invFilter || i.client_name.toLowerCase().includes(invFilter.toLowerCase()) ||
    i.invoice_number.toLowerCase().includes(invFilter.toLowerCase()) || i.status === invFilter
  );
  
  const filteredExp = expenses.filter(e =>
    !expFilter || e.vendor.toLowerCase().includes(expFilter.toLowerCase()) ||
    e.category.toLowerCase().includes(expFilter.toLowerCase()) || e.status === expFilter
  );

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="ab-root">
      <style>{STYLES}</style>

      {/* Confirmation Dialog */}
      {confirmDialog.show && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(prev => ({ ...prev, show: false }))}
        />
      )}

      <div className="ab-topbar ab-no-print">
        <h1>📒 AfaqBooks Online <span className="ab-badge">UAE VAT Compliant</span></h1>
        <button className="ab-btn ab-btn-ghost ab-btn-sm" onClick={() => window.print()}>🖨 Print</button>
      </div>

      <div className="ab-tabs ab-no-print">
        {([
          ["dashboard", "📊 Dashboard"],
          ["invoices", "🧾 Invoices"],
          ["expenses", "💸 Expenses"],
          ["banking", "🏦 Banking"],
          ["reports", "📈 Reports"],
          ["vat", "🇦🇪 VAT & FTA"],
          ["coa", "📋 Chart of Accounts"],
          ["payroll", "👥 Payroll"],
          ["import", "📥 Import"],
          ["online-pl", "💎 Online P&L"],
          ["shopify-pl", "🛍️ Shopify P&L"],
        ] as [Tab, string][]).map(([k, label]) => (
          <button
            key={k}
            className={`ab-tab${tab === k ? " active" : ""}`}
            onClick={() => setTab(k)}
            aria-current={tab === k ? "page" : undefined}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="ab-content">

        {/* ── DASHBOARD ──────────────────────────────────────────────────── */}
        {tab === "dashboard" && (
          <div>
            {loading.dashboard && <LoadingSpinner />}
            {error.dashboard && <ErrorMessage message={error.dashboard} onRetry={loadDash} />}
            
            {!loading.dashboard && !error.dashboard && (
              <>
                <div className="ab-cards">
                  <StatCard label="Total Revenue (MTD)" value={fmt(dash.revenue_month)} />
                  <StatCard label="Total Expenses (MTD)" value={fmt(dash.expenses_month)} variant="danger" />
                  <StatCard 
                    label="Net Profit (MTD)" 
                    value={fmt(dash.net_profit_month)} 
                    variant={(dash.net_profit_month ?? 0) >= 0 ? "success" : "danger"} 
                  />
                  <StatCard 
                    label="Outstanding Invoices" 
                    value={fmt(dash.outstanding_amount)} 
                    sub={`${dash.outstanding_count ?? 0} invoices`} 
                  />
                  <StatCard label="Overdue Amount" value={fmt(dash.overdue_amount)} variant="danger" />
                  <StatCard label="Bank Balance" value={fmt(dash.bank_balance)} variant="success" />
                </div>
                <div className="ab-section-title">Quick Actions</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button className="ab-btn ab-btn-primary" onClick={() => { setTab("invoices"); setShowNewInv(true); }}>+ New Invoice</button>
                  <button className="ab-btn ab-btn-ghost" onClick={() => { setTab("expenses"); setShowNewExp(true); }}>+ Add Expense</button>
                  <button className="ab-btn ab-btn-ghost" onClick={() => setTab("banking")}>🏦 Bank Reconciliation</button>
                  <button className="ab-btn ab-btn-ghost" onClick={() => setTab("vat")}>🇦🇪 VAT Return</button>
                  <button className="ab-btn ab-btn-ghost" onClick={() => setTab("reports")}>📈 Reports</button>
                  <button className="ab-btn ab-btn-ghost" onClick={async () => {
                    try { 
                      const r = await acctApi.shopify?.sync?.({ from_date: firstOfMonth() }); 
                      alert(`Shopify sync: ${JSON.stringify(r)}`); 
                      loadDash();
                    } catch (err: any) {
                      alert(`Shopify sync failed: ${err.message}`);
                    }
                  }}>🛍 Sync Shopify</button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── INVOICES ───────────────────────────────────────────────────── */}
        {tab === "invoices" && (
          <div>
            {loading.invoices && <LoadingSpinner />}
            {error.invoices && <ErrorMessage message={error.invoices} onRetry={loadInvoices} />}
            
            {!loading.invoices && !error.invoices && (
              <>
                <div className="ab-toolbar">
                  <input
                    className="ab-input"
                    placeholder="Search client / invoice #..."
                    value={invFilter}
                    onChange={e => setInvFilter(e.target.value)}
                    style={{ flex: 1, minWidth: 180 }}
                    aria-label="Search invoices"
                  />
                  <select
                    className="ab-select"
                    value={invFilter}
                    onChange={e => setInvFilter(e.target.value)}
                    aria-label="Filter by status"
                  >
                    <option value="">All statuses</option>
                    {["draft", "sent", "partial", "paid", "overdue", "void"].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <button className="ab-btn ab-btn-primary" onClick={() => setShowNewInv(true)}>+ New Invoice</button>
                </div>
                <div className="ab-table-wrap">
                  <table className="ab-table">
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>Client</th>
                        <th>Issue Date</th>
                        <th>Due Date</th>
                        <th>Total</th>
                        <th>Balance Due</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInv.map(inv => (
                        <tr key={inv.id}>
                          <td style={{ fontFamily: "monospace", color: "var(--color-primary,#ffd6a5)" }}>
                            {inv.invoice_number}
                          </td>
                          <td>{inv.client_name}</td>
                          <td>{inv.issue_date}</td>
                          <td>{inv.due_date}</td>
                          <td>{fmt(inv.total)}</td>
                          <td>{fmt(inv.balance_due)}</td>
                          <td>{statusBadge(inv.status)}</td>
                          <td style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            {inv.status === "draft" && (
                              <button
                                className="ab-btn ab-btn-ghost ab-btn-sm"
                                onClick={async () => {
                                  try {
                                    await acctApi.invoices.updateStatus(inv.id, "sent");
                                    loadInvoices();
                                  } catch (err: any) {
                                    alert(`Failed to send invoice: ${err.message}`);
                                  }
                                }}
                              >
                                Send
                              </button>
                            )}
                            {["sent", "partial"].includes(inv.status) && (
                              <button
                                className="ab-btn ab-btn-primary ab-btn-sm"
                                onClick={async () => {
                                  try {
                                    await acctApi.invoices.updateStatus(inv.id, "paid");
                                    loadInvoices();
                                  } catch (err: any) {
                                    alert(`Failed to mark as paid: ${err.message}`);
                                  }
                                }}
                              >
                                Mark Paid
                              </button>
                            )}
                            <button className="ab-btn ab-btn-ghost ab-btn-sm" onClick={() => window.print()}>🖨</button>
                          </td>
                        </tr>
                      ))}
                      {filteredInv.length === 0 && (
                        <tr>
                          <td colSpan={8} style={{ textAlign: "center", color: "#8892b0", padding: "32px" }}>
                            No invoices found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* New Invoice Modal */}
        {showNewInv && (
          <div className="ab-overlay" onClick={e => { if (e.target === e.currentTarget) setShowNewInv(false); }}>
            <div className="ab-modal">
              <h2>🧾 New Tax Invoice</h2>
              <div className="ab-form-grid">
                <div className="ab-form-group">
                  <label htmlFor="client_name">Client Name *</label>
                  <input
                    id="client_name"
                    className="ab-input"
                    value={invForm.client_name}
                    onChange={e => setInvForm(f => ({ ...f, client_name: e.target.value }))}
                    required
                    aria-required="true"
                  />
                </div>
                <div className="ab-form-group">
                  <label htmlFor="client_email">Client Email</label>
                  <input
                    id="client_email"
                    className="ab-input"
                    type="email"
                    value={invForm.client_email}
                    onChange={e => setInvForm(f => ({ ...f, client_email: e.target.value }))}
                  />
                </div>
                <div className="ab-form-group">
                  <label htmlFor="client_trn">Client TRN (15 digits)</label>
                  <input
                    id="client_trn"
                    className="ab-input"
                    value={invForm.client_trn}
                    onChange={e => { 
                      setInvForm(f => ({ ...f, client_trn: e.target.value })); 
                      validateTrn(e.target.value); 
                    }}
                  />
                  {trnStatus === "ok" && <span className="ab-notice ok">✓ Valid TRN format</span>}
                  {trnStatus === "warn" && <span className="ab-notice warn">⚠ Invalid TRN format</span>}
                </div>
                <div className="ab-form-group">
                  <label htmlFor="issue_date">Issue Date</label>
                  <input
                    id="issue_date"
                    className="ab-input"
                    type="date"
                    value={invForm.issue_date}
                    onChange={e => setInvForm(f => ({ ...f, issue_date: e.target.value }))}
                  />
                </div>
                <div className="ab-form-group">
                  <label htmlFor="due_date">Due Date</label>
                  <input
                    id="due_date"
                    className="ab-input"
                    type="date"
                    value={invForm.due_date}
                    onChange={e => setInvForm(f => ({ ...f, due_date: e.target.value }))}
                  />
                </div>
                <div className="ab-form-group">
                  <label htmlFor="payment_terms">Payment Terms</label>
                  <select
                    id="payment_terms"
                    className="ab-select"
                    value={invForm.payment_terms}
                    onChange={e => setInvForm(f => ({ ...f, payment_terms: e.target.value }))}
                  >
                    {["Immediate", "Net 15", "Net 30", "Net 60"].map(t => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="ab-form-group span2">
                  <label htmlFor="notes">Notes</label>
                  <textarea
                    id="notes"
                    className="ab-input"
                    rows={2}
                    value={invForm.notes}
                    onChange={e => setInvForm(f => ({ ...f, notes: e.target.value }))}
                  />
                </div>
              </div>

              <div className="ab-section-title" style={{ marginTop: 20 }}>Line Items</div>
              <table className="ab-line-table">
                <thead>
                  <tr>
                    <th style={{ width: "35%" }}>Description</th>
                    <th style={{ width: "10%" }}>Qty</th>
                    <th style={{ width: "17%" }}>Unit Price (AED)</th>
                    <th style={{ width: "12%" }}>VAT %</th>
                    <th style={{ width: "18%" }}>Amount (AED)</th>
                    <th style={{ width: "5%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {invLines.map((l, i) => (
                    <tr key={i}>
                      <td>
                        <input
                          className="ab-input"
                          style={{ width: "100%" }}
                          value={l.description}
                          onChange={e => updateInvLine(i, "description", e.target.value)}
                          placeholder="Item description"
                        />
                      </td>
                      <td>
                        <input
                          className="ab-input"
                          type="number"
                          min="0"
                          value={l.quantity}
                          onChange={e => updateInvLine(i, "quantity", parseFloat(e.target.value) || 0)}
                        />
                      </td>
                      <td>
                        <input
                          className="ab-input"
                          type="number"
                          min="0"
                          step="0.01"
                          value={l.unit_price}
                          onChange={e => updateInvLine(i, "unit_price", parseFloat(e.target.value) || 0)}
                        />
                      </td>
                      <td>
                        <select
                          className="ab-select"
                          value={l.vat_rate}
                          onChange={e => updateInvLine(i, "vat_rate", parseFloat(e.target.value))}
                        >
                          <option value={5}>5% SR</option>
                          <option value={0}>0% ZR</option>
                        </select>
                      </td>
                      <td style={{ textAlign: "right", fontFamily: "monospace" }}>
                        {(l.quantity * l.unit_price).toFixed(2)}
                      </td>
                      <td>
                        <button
                          className="ab-btn ab-btn-danger ab-btn-sm"
                          onClick={() => setInvLines(ls => ls.filter((_, j) => j !== i))}
                          disabled={invLines.length <= 1}
                          aria-label="Remove line item"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                className="ab-btn ab-btn-ghost ab-btn-sm"
                style={{ marginTop: 8 }}
                onClick={() => setInvLines(ls => [...ls, { description: "", quantity: 1, unit_price: 0, vat_rate: 5 }])}
              >
                + Add Line
              </button>

              <div className="ab-totals">
                <div>Subtotal: <strong>{fmt(invSubtotal)}</strong></div>
                <div>VAT: <strong>{fmt(invVat)}</strong></div>
                <div style={{ fontSize: "1.1rem" }}>Total: <strong>{fmt(invTotal)}</strong></div>
              </div>
              <p className="ab-notice">This will be marked as UAE TAX INVOICE as required by FTA.</p>
              <div className="ab-modal-footer">
                <button className="ab-btn ab-btn-ghost" onClick={() => setShowNewInv(false)}>Cancel</button>
                <button
                  className="ab-btn ab-btn-primary"
                  onClick={saveInvoice}
                  disabled={loading.saveInvoice}
                >
                  {loading.saveInvoice ? "Saving…" : "Save Invoice"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── EXPENSES ───────────────────────────────────────────────────── */}
        {tab === "expenses" && (
          <div>
            {loading.expenses && <LoadingSpinner />}
            {error.expenses && <ErrorMessage message={error.expenses} onRetry={loadExpenses} />}
            
            {!loading.expenses && !error.expenses && (
              <>
                <div className="ab-toolbar">
                  <input
                    className="ab-input"
                    placeholder="Search vendor / category..."
                    value={expFilter}
                    onChange={e => setExpFilter(e.target.value)}
                    style={{ flex: 1, minWidth: 180 }}
                    aria-label="Search expenses"
                  />
                  <select
                    className="ab-select"
                    value={expFilter}
                    onChange={e => setExpFilter(e.target.value)}
                    aria-label="Filter by status"
                  >
                    <option value="">All statuses</option>
                    {["pending", "approved", "paid", "rejected"].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <button className="ab-btn ab-btn-primary" onClick={() => setShowNewExp(true)}>+ Add Expense</button>
                </div>
                <div className="ab-table-wrap">
                  <table className="ab-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Vendor</th>
                        <th>Category</th>
                        <th>Amount</th>
                        <th>VAT</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredExp.map(e => (
                        <tr key={e.id}>
                          <td>{e.date}</td>
                          <td>{e.vendor}</td>
                          <td><span className="ab-badge">{e.category}</span></td>
                          <td>{fmt(e.amount)}</td>
                          <td>{fmt(e.vat_amount)}</td>
                          <td>{fmt(e.total)}</td>
                          <td>{statusBadge(e.status)}</td>
                          <td>
                            {e.status === "pending" && (
                              <button
                                className="ab-btn ab-btn-primary ab-btn-sm"
                                onClick={async () => {
                                  try {
                                    await acctApi.expenses.approve(e.id);
                                    loadExpenses();
                                  } catch (err: any) {
                                    alert(`Failed to approve expense: ${err.message}`);
                                  }
                                }}
                              >
                                Approve
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {filteredExp.length === 0 && (
                        <tr>
                          <td colSpan={8} style={{ textAlign: "center", color: "#8892b0", padding: "32px" }}>
                            No expenses found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* New Expense Modal */}
        {showNewExp && (
          <div className="ab-overlay" onClick={e => { if (e.target === e.currentTarget) setShowNewExp(false); }}>
            <div className="ab-modal">
              <h2>💸 Add Expense</h2>
              <div className="ab-form-grid">
                <div className="ab-form-group">
                  <label htmlFor="exp_date">Date *</label>
                  <input
                    id="exp_date"
                    className="ab-input"
                    type="date"
                    value={expForm.date}
                    onChange={e => setExpForm(f => ({ ...f, date: e.target.value }))}
                    required
                    aria-required="true"
                  />
                </div>
                <div className="ab-form-group">
                  <label htmlFor="exp_vendor">Vendor *</label>
                  <input
                    id="exp_vendor"
                    className="ab-input"
                    value={expForm.vendor}
                    onChange={e => setExpForm(f => ({ ...f, vendor: e.target.value }))}
                    required
                    aria-required="true"
                  />
                </div>
                <div className="ab-form-group">
                  <label htmlFor="exp_vendor_trn">Vendor TRN (for VAT recovery)</label>
                  <input
                    id="exp_vendor_trn"
                    className="ab-input"
                    value={expForm.vendor_trn}
                    onChange={e => setExpForm(f => ({ ...f, vendor_trn: e.target.value }))}
                  />
                </div>
                <div className="ab-form-group">
                  <label htmlFor="exp_category">Category *</label>
                  <select
                    id="exp_category"
                    className="ab-select"
                    value={expForm.category}
                    onChange={e => setExpForm(f => ({ ...f, category: e.target.value }))}
                    required
                    aria-required="true"
                  >
                    <option value="">Select category</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="ab-form-group">
                  <label htmlFor="exp_amount">Amount excl. VAT (AED) *</label>
                  <input
                    id="exp_amount"
                    className="ab-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={expForm.amount}
                    onChange={e => {
                      const a = e.target.value;
                      setExpForm(f => ({
                        ...f,
                        amount: a,
                        vat_amount: String(Math.round(parseFloat(a || "0") * 0.05 * 100) / 100)
                      }));
                    }}
                    required
                    aria-required="true"
                  />
                </div>
                <div className="ab-form-group">
                  <label htmlFor="exp_vat">VAT Amount (AED)</label>
                  <input
                    id="exp_vat"
                    className="ab-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={expForm.vat_amount}
                    onChange={e => setExpForm(f => ({ ...f, vat_amount: e.target.value }))}
                  />
                </div>
                <div className="ab-form-group span2">
                  <label htmlFor="exp_notes">Notes</label>
                  <textarea
                    id="exp_notes"
                    className="ab-input"
                    rows={2}
                    value={expForm.notes}
                    onChange={e => setExpForm(f => ({ ...f, notes: e.target.value }))}
                  />
                </div>
                <div className="ab-form-group" style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                  <input
                    type="checkbox"
                    id="exp_recurring"
                    checked={expForm.is_recurring}
                    onChange={e => setExpForm(f => ({ ...f, is_recurring: e.target.checked }))}
                  />
                  <label htmlFor="exp_recurring" style={{ margin: 0 }}>Recurring expense</label>
                </div>
                {expForm.is_recurring && (
                  <div className="ab-form-group">
                    <label htmlFor="exp_interval">Interval</label>
                    <select
                      id="exp_interval"
                      className="ab-select"
                      value={expForm.recur_interval}
                      onChange={e => setExpForm(f => ({ ...f, recur_interval: e.target.value }))}
                    >
                      {["weekly", "monthly", "quarterly"].map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div style={{ marginTop: 14, textAlign: "right" }}>
                Total incl. VAT: <strong style={{ color: "var(--color-primary,#ffd6a5)" }}>
                  {fmt((parseFloat(expForm.amount) || 0) + (parseFloat(expForm.vat_amount) || 0))}
                </strong>
              </div>
              <div className="ab-modal-footer">
                <button className="ab-btn ab-btn-ghost" onClick={() => setShowNewExp(false)}>Cancel</button>
                <button
                  className="ab-btn ab-btn-primary"
                  onClick={saveExpense}
                  disabled={loading.saveExpense}
                >
                  {loading.saveExpense ? "Saving…" : "Save Expense"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── BANKING ────────────────────────────────────────────────────── */}
        {tab === "banking" && (
          <div>
            {loading.banking && <LoadingSpinner />}
            {error.banking && <ErrorMessage message={error.banking} onRetry={loadBankTxs} />}
            
            {!loading.banking && !error.banking && (
              <>
                <div className="ab-toolbar">
                  <select
                    className="ab-select"
                    value={selectedBankAcct}
                    onChange={e => setSelectedBankAcct(e.target.value)}
                    aria-label="Select bank account"
                  >
                    <option value="">All bank accounts</option>
                    {bankAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.name} ({a.code})</option>
                    ))}
                  </select>
                  <select
                    className="ab-select"
                    value={bankFilter}
                    onChange={e => setBankFilter(e.target.value)}
                    aria-label="Filter by status"
                  >
                    {["unmatched", "matched", "excluded"].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <label className="ab-btn ab-btn-ghost" style={{ cursor: "pointer" }}>
                    📤 Import CSV
                    <input
                      type="file"
                      accept=".csv"
                      style={{ display: "none" }}
                      onChange={async e => {
                        const f = e.target.files?.[0];
                        if (!f || !selectedBankAcct) {
                          alert("Select a bank account first");
                          return;
                        }
                        const text = await f.text();
                        try {
                          await acctApi.bank.import({ account_id: selectedBankAcct, csv_content: text });
                          loadBankTxs();
                        } catch (err: any) {
                          alert(`CSV import failed: ${err.message}`);
                        }
                      }}
                    />
                  </label>
                </div>
                <div className="ab-table-wrap">
                  <table className="ab-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Amount</th>
                        <th>Reference</th>
                        <th>Suggested Category</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bankTxs.map(tx => (
                        <tr key={tx.id}>
                          <td>{tx.date}</td>
                          <td>{tx.description}</td>
                          <td style={{
                            color: tx.amount >= 0 ? "var(--color-tertiary,#b8d58d)" : "#ff6b6b",
                            fontFamily: "monospace"
                          }}>
                            {tx.amount >= 0 ? "+" : ""}{fmt(tx.amount)}
                          </td>
                          <td style={{ fontFamily: "monospace", fontSize: ".78rem" }}>{tx.reference}</td>
                          <td><span className="ab-badge">{tx.suggested_category || "—"}</span></td>
                          <td>{statusBadge(tx.status)}</td>
                          <td style={{ display: "flex", gap: 4 }}>
                            {tx.status === "unmatched" && (
                              <>
                                <button
                                  className="ab-btn ab-btn-primary ab-btn-sm"
                                  onClick={async () => {
                                    try {
                                      await acctApi.bank.match({ bank_tx_id: tx.id, create_journal: true });
                                      loadBankTxs();
                                    } catch (err: any) {
                                      alert(`Failed to match transaction: ${err.message}`);
                                    }
                                  }}
                                >
                                  Match
                                </button>
                                <button
                                  className="ab-btn ab-btn-ghost ab-btn-sm"
                                  onClick={async () => {
                                    try {
                                      await acctApi.bank.exclude(tx.id);
                                      loadBankTxs();
                                    } catch (err: any) {
                                      alert(`Failed to exclude transaction: ${err.message}`);
                                    }
                                  }}
                                >
                                  Exclude
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                      {bankTxs.length === 0 && (
                        <tr>
                          <td colSpan={7} style={{ textAlign: "center", color: "#8892b0", padding: "40px" }}>
                            No transactions. Select a bank account and import a CSV file to start reconciliation.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── REPORTS ────────────────────────────────────────────────────── */}
        {tab === "reports" && (
          <div>
            <div className="ab-subtabs">
              {([
                ["pl", "P&L"], ["bs", "Balance Sheet"], ["tb", "Trial Balance"], ["ar", "A/R Aging"], ["cf", "Cash Flow"],
              ] as [ReportTab, string][]).map(([k, label]) => (
                <button
                  key={k}
                  className={`ab-subtab${reportTab === k ? " active" : ""}`}
                  onClick={() => setReportTab(k)}
                >
                  {label}
                </button>
              ))}
            </div>

            {(reportTab === "pl" || reportTab === "cf") && (
              <div className="ab-toolbar" style={{ marginBottom: 16 }}>
                <label style={{ fontSize: ".82rem", color: "#8892b0" }}>From</label>
                <input
                  className="ab-input"
                  type="date"
                  value={reportFrom}
                  onChange={e => setReportFrom(e.target.value)}
                />
                <label style={{ fontSize: ".82rem", color: "#8892b0" }}>To</label>
                <input
                  className="ab-input"
                  type="date"
                  value={reportTo}
                  onChange={e => setReportTo(e.target.value)}
                />
                <button className="ab-btn ab-btn-primary" onClick={loadReport}>Run</button>
                <button className="ab-btn ab-btn-ghost" onClick={() => window.print()}>🖨 Print</button>
              </div>
            )}

            {loading.reports && <LoadingSpinner />}
            {error.reports && <ErrorMessage message={error.reports} onRetry={loadReport} />}

            {!loading.reports && !error.reports && reportData && (
              <div>
                {/* P&L */}
                {reportTab === "pl" && (
                  <div>
                    <h3 style={{ color: "var(--color-primary,#ffd6a5)", margin: "0 0 16px" }}>
                      Profit & Loss — {reportFrom} to {reportTo}
                    </h3>
                    <table className="ab-table">
                      <thead>
                        <tr>
                          <th>Account</th>
                          <th>Code</th>
                          <th style={{ textAlign: "right" }}>Amount (AED)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td colSpan={3} style={{ color: "#8892b0", fontWeight: 600, paddingTop: 8 }}>Revenue</td>
                        </tr>
                        {(reportData.revenue_lines || []).map((r: any, i: number) => (
                          <tr key={i}>
                            <td style={{ paddingLeft: 24 }}>{r.name}</td>
                            <td style={{ fontFamily: "monospace", color: "#8892b0" }}>{r.code}</td>
                            <td style={{ textAlign: "right", fontFamily: "monospace" }}>{fmt(r.amount)}</td>
                          </tr>
                        ))}
                        <tr style={{ fontWeight: 700 }}>
                          <td>Total Revenue</td>
                          <td></td>
                          <td style={{ textAlign: "right", fontFamily: "monospace", color: "var(--color-tertiary,#b8d58d)" }}>
                            {fmt(reportData.total_revenue)}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3} style={{ color: "#8892b0", fontWeight: 600, paddingTop: 12 }}>Expenses</td>
                        </tr>
                        {(reportData.expense_lines || []).map((r: any, i: number) => (
                          <tr key={i}>
                            <td style={{ paddingLeft: 24 }}>{r.name}</td>
                            <td style={{ fontFamily: "monospace", color: "#8892b0" }}>{r.code}</td>
                            <td style={{ textAlign: "right", fontFamily: "monospace" }}>{fmt(r.amount)}</td>
                          </tr>
                        ))}
                        <tr style={{ fontWeight: 700 }}>
                          <td>Total Expenses</td>
                          <td></td>
                          <td style={{ textAlign: "right", fontFamily: "monospace", color: "#ff6b6b" }}>
                            {fmt(reportData.total_expenses)}
                          </td>
                        </tr>
                        <tr style={{ fontWeight: 700, fontSize: "1.05rem", borderTop: "2px solid rgba(255,255,255,.1)" }}>
                          <td>Net Profit</td>
                          <td></td>
                          <td style={{ textAlign: "right", fontFamily: "monospace", color: "var(--color-primary,#ffd6a5)" }}>
                            {fmt(reportData.net_profit)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Balance Sheet */}
                {reportTab === "bs" && (
                  <div>
                    <h3 style={{ color: "var(--color-primary,#ffd6a5)", margin: "0 0 16px" }}>
                      Balance Sheet — As at {todayStr()}
                    </h3>
                    {(["assets", "liabilities", "equity"] as const).map(section => (
                      <div key={section} style={{ marginBottom: 24 }}>
                        <div className="ab-section-title" style={{ textTransform: "capitalize" }}>{section}</div>
                        <table className="ab-table">
                          <tbody>
                            {(reportData[section] || []).map((r: any, i: number) => (
                              <tr key={i}>
                                <td>{r.name}</td>
                                <td style={{ fontFamily: "monospace", color: "#8892b0" }}>{r.code}</td>
                                <td style={{ textAlign: "right", fontFamily: "monospace" }}>{fmt(r.balance)}</td>
                              </tr>
                            ))}
                            <tr style={{ fontWeight: 700 }}>
                              <td>Total {section}</td>
                              <td></td>
                              <td style={{ textAlign: "right", fontFamily: "monospace", color: "var(--color-primary,#ffd6a5)" }}>
                                {fmt(reportData[`total_${section}`])}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                )}

                {/* Trial Balance */}
                {reportTab === "tb" && (
                  <div>
                    <h3 style={{ color: "var(--color-primary,#ffd6a5)", margin: "0 0 16px" }}>
                      Trial Balance — As at {todayStr()}
                    </h3>
                    <table className="ab-table">
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Account</th>
                          <th style={{ textAlign: "right" }}>Debit</th>
                          <th style={{ textAlign: "right" }}>Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(reportData.accounts || []).map((r: any, i: number) => (
                          <tr key={i}>
                            <td style={{ fontFamily: "monospace", color: "#8892b0" }}>{r.code}</td>
                            <td>{r.name}</td>
                            <td style={{ textAlign: "right", fontFamily: "monospace" }}>{r.debit > 0 ? fmt(r.debit) : ""}</td>
                            <td style={{ textAlign: "right", fontFamily: "monospace" }}>{r.credit > 0 ? fmt(r.credit) : ""}</td>
                          </tr>
                        ))}
                        <tr style={{ fontWeight: 700, borderTop: "2px solid rgba(255,255,255,.1)" }}>
                          <td colSpan={2}>Totals</td>
                          <td style={{ textAlign: "right", fontFamily: "monospace", color: "var(--color-primary,#ffd6a5)" }}>
                            {fmt(reportData.total_debits)}
                          </td>
                          <td style={{ textAlign: "right", fontFamily: "monospace", color: "var(--color-primary,#ffd6a5)" }}>
                            {fmt(reportData.total_credits)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {(reportTab === "ar" || reportTab === "cf") && (
                  <pre style={{
                    color: "#8892b0",
                    fontSize: ".8rem",
                    overflowX: "auto",
                    background: "rgba(255,255,255,.03)",
                    padding: 16,
                    borderRadius: 8
                  }}>
                    {JSON.stringify(reportData, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── VAT & FTA ──────────────────────────────────────────────────── */}
        {tab === "vat" && (
          <div>
            {loading.vat && <LoadingSpinner />}
            {error.vat && <ErrorMessage message={error.vat} onRetry={loadVatSummary} />}
            
            {!loading.vat && !error.vat && (
              <>
                {vatSummary && (
                  <div className="ab-cards" style={{ marginBottom: 24 }}>
                    <StatCard label="Output VAT (Sales)" value={fmt(vatSummary.output_tax)} />
                    <StatCard label="Input VAT (Purchases)" value={fmt(vatSummary.input_tax)} variant="success" />
                    <StatCard
                      label="Net VAT Payable"
                      value={fmt(vatSummary.net_payable)}
                      variant={(vatSummary.net_payable ?? 0) > 0 ? "danger" : "success"}
                      sub={`Due: ${vatSummary.next_due_date ?? "—"}`}
                    />
                  </div>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                  {/* VAT Return calculator */}
                  <div className="ab-card">
                    <div className="ab-section-title">🧮 VAT Return (Form VAT201)</div>
                    <div className="ab-form-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
                      <div className="ab-form-group">
                        <label htmlFor="vat_from">Period From</label>
                        <input
                          id="vat_from"
                          className="ab-input"
                          type="date"
                          value={vatPeriodFrom}
                          onChange={e => setVatPeriodFrom(e.target.value)}
                        />
                      </div>
                      <div className="ab-form-group">
                        <label htmlFor="vat_to">Period To</label>
                        <input
                          id="vat_to"
                          className="ab-input"
                          type="date"
                          value={vatPeriodTo}
                          onChange={e => setVatPeriodTo(e.target.value)}
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
                      <button
                        className="ab-btn ab-btn-ghost"
                        onClick={calcVatReturn}
                        disabled={loading.vatCalc}
                      >
                        {loading.vatCalc ? "Calculating…" : "Calculate"}
                      </button>
                      {vatReturn && (
                        <button
                          className="ab-btn ab-btn-primary"
                          onClick={submitVatReturn}
                          disabled={loading.vatSubmit}
                        >
                          {loading.vatSubmit ? "Submitting…" : "Submit to EmaraTax"}
                        </button>
                      )}
                    </div>
                    {vatReturn && (
                      <div style={{ marginTop: 16, fontSize: ".84rem", lineHeight: 2.2 }}>
                        <div>Standard Rated Sales: <strong>{fmt(vatReturn.standard_rated_sales)}</strong></div>
                        <div>Output VAT (5%): <strong>{fmt(vatReturn.standard_rated_vat)}</strong></div>
                        <div>Zero Rated Sales: <strong>{fmt(vatReturn.zero_rated_sales)}</strong></div>
                        <div>Exempt Sales: <strong>{fmt(vatReturn.exempt_sales)}</strong></div>
                        <div>Input VAT Recoverable: <strong style={{ color: "#b8d58d" }}>{fmt(vatReturn.input_vat_recoverable)}</strong></div>
                        <div style={{ fontSize: "1rem", fontWeight: 700, marginTop: 8 }}>
                          Net VAT Payable: <strong style={{ color: "var(--color-primary,#ffd6a5)" }}>{fmt(vatReturn.net_vat_payable)}</strong>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* TRN Validator */}
                  <div className="ab-card">
                    <div className="ab-section-title">🔍 TRN Validator</div>
                    <p style={{ fontSize: ".82rem", color: "#8892b0", margin: "0 0 12px" }}>
                      Validate UAE Tax Registration Numbers (15 digits, starts with 100)
                    </p>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        className="ab-input"
                        style={{ flex: 1 }}
                        placeholder="e.g. 100123456789012"
                        value={trnInput}
                        onChange={e => setTrnInput(e.target.value)}
                        aria-label="TRN number"
                      />
                      <button
                        className="ab-btn ab-btn-primary"
                        onClick={lookupTrn}
                        disabled={loading.trnLookup}
                      >
                        {loading.trnLookup ? "Validating…" : "Validate"}
                      </button>
                    </div>
                    {trnResult && (
                      <div style={{
                        marginTop: 14,
                        padding: "12px 16px",
                        borderRadius: 8,
                        background: trnResult.valid ? "rgba(184,213,141,.1)" : "rgba(255,107,107,.1)",
                        border: `1px solid ${trnResult.valid ? "#b8d58d" : "#ff6b6b"}33`
                      }}>
                        <div style={{ fontWeight: 700, color: trnResult.valid ? "#b8d58d" : "#ff6b6b" }}>
                          {trnResult.valid ? "✓ Valid TRN" : "✗ Invalid TRN"}
                        </div>
                        <div style={{ fontSize: ".8rem", color: "#8892b0", marginTop: 4 }}>{trnResult.message}</div>
                        {trnResult.business_name && (
                          <div style={{ marginTop: 6 }}><strong>Business:</strong> {trnResult.business_name}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── CHART OF ACCOUNTS ──────────────────────────────────────────── */}
        {tab === "coa" && (
          <div>
            {loading.coa && <LoadingSpinner />}
            {error.coa && <ErrorMessage message={error.coa} onRetry={loadAccounts} />}
            
            {!loading.coa && !error.coa && (
              <>
                <div className="ab-toolbar">
                  <button
                    className="ab-btn ab-btn-primary"
                    onClick={async () => {
                      try {
                        await acctApi.accounts.setup();
                        setSetupDone(true);
                        loadAccounts();
                      } catch (err: any) {
                        alert(`Setup failed: ${err.message}`);
                      }
                    }}
                  >
                    ⚙ Setup Default UAE CoA
                  </button>
                  {setupDone && <span className="ab-notice ok">✓ Default chart of accounts loaded</span>}
                  <button className="ab-btn ab-btn-ghost" onClick={loadAccounts}>↻ Refresh</button>
                </div>
                {Object.entries(coaGroups).sort().map(([type, accts]) => (
                  <div className="ab-coa-group" key={type}>
                    <div className="ab-coa-type">{type.replace(/_/g, " ")}</div>
                    {[...accts].sort((a, b) => a.code.localeCompare(b.code)).map(a => (
                      <div className="ab-coa-row" key={a.id}>
                        <span className="ab-coa-code">{a.code}</span>
                        <span className="ab-coa-name">{a.name}</span>
                        {a.is_bank && <span className="ab-badge">BANK</span>}
                        <span
                          className="ab-coa-balance"
                          style={{
                            color: a.balance > 0 ? "var(--color-tertiary,#b8d58d)" : a.balance < 0 ? "#ff6b6b" : "#8892b0"
                          }}
                        >
                          {fmt(a.balance)}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
                {accounts.length === 0 && (
                  <div style={{ textAlign: "center", color: "#8892b0", padding: "48px 0" }}>
                    No accounts yet. Click <strong>"Setup Default UAE CoA"</strong> to seed 30 standard accounts.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── PAYROLL ────────────────────────────────────────────────────── */}
        {tab === "payroll" && (
          <div>
            {loading.payroll && <LoadingSpinner />}
            {error.payroll && <ErrorMessage message={error.payroll} onRetry={runPayroll} />}
            
            {!loading.payroll && !error.payroll && (
              <>
                <div className="ab-toolbar">
                  <label style={{ fontSize: ".82rem", color: "#8892b0" }}>Payroll Month</label>
                  <input
                    className="ab-input"
                    type="month"
                    value={payrollMonth}
                    onChange={e => setPayrollMonth(e.target.value)}
                    aria-label="Select payroll month"
                  />
                  <button
                    className="ab-btn ab-btn-primary"
                    onClick={runPayroll}
                    disabled={loading.payroll}
                  >
                    {loading.payroll ? "Calculating…" : "Calculate Payroll"}
                  </button>
                  {payrollResult.length > 0 && (
                    <button
                      className="ab-btn ab-btn-ghost"
                      onClick={async () => {
                        try {
                          await acctApi.payroll.exportWps({ month: payrollMonth });
                          alert("WPS file exported");
                        } catch (err: any) {
                          alert(`WPS export failed: ${err.message}`);
                        }
                      }}
                    >
                      📤 Export WPS
                    </button>
                  )}
                </div>

                {payrollResult.length > 0 && (
                  <>
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr",
                      gap: 10,
                      padding: "8px 14px",
                      fontSize: ".75rem",
                      color: "#8892b0",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: ".05em"
                    }}>
                      <span>Employee</span>
                      <span>Basic</span>
                      <span>Housing (25%)</span>
                      <span>Transport (10%)</span>
                      <span>Gratuity</span>
                      <span>Net Pay</span>
                    </div>
                    {payrollResult.map((p: any, i: number) => (
                      <div className="ab-payroll-row" key={i}>
                        <span style={{ fontWeight: 600 }}>{p.name}</span>
                        <span style={{ fontFamily: "monospace" }}>{fmt(p.base_salary)}</span>
                        <span style={{ fontFamily: "monospace" }}>{fmt(p.housing)}</span>
                        <span style={{ fontFamily: "monospace" }}>{fmt(p.transport)}</span>
                        <span style={{ fontFamily: "monospace", color: "#8892b0" }}>{fmt(p.gratuity_month)}</span>
                        <span style={{ fontFamily: "monospace", color: "var(--color-primary,#ffd6a5)", fontWeight: 700 }}>
                          {fmt(p.net_pay)}
                        </span>
                      </div>
                    ))}
                    <div style={{ marginTop: 16, textAlign: "right", fontSize: "1rem" }}>
                      Total Payroll: <strong style={{ color: "var(--color-primary,#ffd6a5)" }}>
                        {fmt(payrollResult.reduce((s: number, p: any) => s + (p.net_pay || 0), 0))}
                      </strong>
                    </div>
                  </>
                )}

                {payrollResult.length === 0 && (
                  <div style={{ textAlign: "center", color: "#8892b0", padding: "48px 0" }}>
                    Select a month and click <strong>Calculate Payroll</strong> to compute UAE payroll with housing (25%) + transport (10%) allowances and gratuity accrual.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── IMPORT ─────────────────────────────────────────────────────── */}
        {tab === "import" && (
          <div>
            <div className="ab-section-title">Import Engine · CSV · PDF · Receipt OCR</div>
            <div className="ab-import-grid">

              {/* A — CSV */}
              <div className="ab-import-card">
                <h3>📊 CSV · Bulk Import</h3>
                <label className="ab-drop">
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={e => onPickCsv(e.target.files?.[0] || null)}
                  />
                  {impCsvFile ? (
                    <>
                      Drop a different CSV or click to replace
                      <span className="ab-drop-name">{impCsvFile.name}</span>
                    </>
                  ) : (
                    <>
                      Drag & drop a CSV here, or click to browse<br />
                      <span style={{ fontSize: ".72rem" }}>Google Ads · Expenses · Sales · Other</span>
                    </>
                  )}
                </label>
                <div className="ab-imp-row">
                  <label>Category</label>
                  <select
                    className="ab-select"
                    value={impCsvCategory}
                    onChange={e => setImpCsvCategory(e.target.value)}
                  >
                    <option>Google Ads</option>
                    <option>Expenses</option>
                    <option>Sales</option>
                    <option>Other</option>
                  </select>
                </div>
                {impCsvPreview.length > 0 && (
                  <div className="ab-imp-preview">
                    <table>
                      <thead>
                        <tr>{(impCsvPreview[0] || []).map((h, i) => <th key={i}>{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {impCsvPreview.slice(1, 6).map((r, i) => (
                          <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <button
                  className="ab-btn ab-btn-primary"
                  disabled={!impCsvFile || loading.csvImport}
                  onClick={runCsvImport}
                >
                  {loading.csvImport ? "Importing…" : "Import CSV"}
                </button>
                {impCsvResult && !impCsvResult.error && (
                  <div className="ab-imp-result">
                    ✅ {impCsvResult.rows_imported} rows — AED {(impCsvResult.total_aed ?? 0).toLocaleString()}
                    {impCsvResult.skipped > 0 && <> · {impCsvResult.skipped} skipped</>}
                    {impCsvResult.csv_type && <> · type: {impCsvResult.csv_type}</>}
                  </div>
                )}
                {impCsvResult?.error && (
                  <div className="ab-imp-result err">⚠ {impCsvResult.error}</div>
                )}
              </div>

              {/* B — PDF */}
              <div className="ab-import-card">
                <h3>📄 PDF Invoice</h3>
                <label className="ab-drop">
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={e => { setImpPdfFile(e.target.files?.[0] || null); setImpPdfResult(null); }}
                  />
                  {impPdfFile ? (
                    <>
                      Ready to extract
                      <span className="ab-drop-name">{impPdfFile.name}</span>
                    </>
                  ) : (
                    <>
                      Drop a PDF invoice · max 10 MB<br />
                      <span style={{ fontSize: ".72rem" }}>AI extracts vendor, date, total, VAT</span>
                    </>
                  )}
                </label>
                <div className="ab-imp-row">
                  <label>Category</label>
                  <input
                    className="ab-input"
                    value={impPdfCategory}
                    onChange={e => setImpPdfCategory(e.target.value)}
                  />
                </div>
                <button
                  className="ab-btn ab-btn-primary"
                  disabled={!impPdfFile || loading.pdfImport}
                  onClick={runPdfImport}
                >
                  {loading.pdfImport ? "Extracting…" : "Extract & Book"}
                </button>
                {impPdfResult && !impPdfResult.error && (
                  <div className="ab-imp-result">
                    <div className="ab-imp-field"><span>Vendor</span><strong>{impPdfResult.vendor}</strong></div>
                    <div className="ab-imp-field"><span>Date</span>{impPdfResult.date}</div>
                    <div className="ab-imp-field"><span>Total</span>AED {Number(impPdfResult.amount_aed || 0).toLocaleString()}</div>
                    <div className="ab-imp-field"><span>VAT</span>AED {Number(impPdfResult.vat || 0).toLocaleString()}</div>
                    <div className="ab-imp-field"><span>Lines</span>{impPdfResult.lines_imported}</div>
                  </div>
                )}
                {impPdfResult?.error && (
                  <div className="ab-imp-result err">⚠ {impPdfResult.error}</div>
                )}
              </div>

              {/* C — Image OCR */}
              <div className="ab-import-card">
                <h3>🧾 Receipt OCR</h3>
                <label className="ab-drop">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                    onChange={e => { setImpImgFile(e.target.files?.[0] || null); setImpImgResult(null); }}
                  />
                  {impImgFile ? (
                    <>
                      Ready for OCR
                      <span className="ab-drop-name">{impImgFile.name}</span>
                    </>
                  ) : (
                    <>
                      Drop a receipt photo · JPG · PNG · WEBP<br />
                      <span style={{ fontSize: ".72rem" }}>Vision AI reads vendor, total, VAT</span>
                    </>
                  )}
                </label>
                <div className="ab-imp-row">
                  <label>Category</label>
                  <input
                    className="ab-input"
                    value={impImgCategory}
                    onChange={e => setImpImgCategory(e.target.value)}
                  />
                </div>
                <button
                  className="ab-btn ab-btn-primary"
                  disabled={!impImgFile || loading.imgImport}
                  onClick={runImgImport}
                >
                  {loading.imgImport ? "Scanning…" : "Scan & Book"}
                </button>
                {impImgResult && impImgResult.amount_aed !== undefined && (
                  <div className={`ab-imp-result${impImgResult.error ? " err" : ""}`}>
                    <div className="ab-imp-field"><span>Vendor</span><strong>{impImgResult.vendor}</strong></div>
                    <div className="ab-imp-field"><span>Date</span>{impImgResult.date}</div>
                    <div className="ab-imp-field"><span>Total</span>AED {Number(impImgResult.amount_aed || 0).toLocaleString()}</div>
                    <div className="ab-imp-field"><span>VAT</span>AED {Number(impImgResult.vat || 0).toLocaleString()}</div>
                    {impImgResult.confidence !== undefined && (
                      <div className="ab-imp-field">
                        <span>Confidence</span>
                        <span className="ab-imp-confidence">
                          {Math.round((impImgResult.confidence || 0) * 100)}%
                        </span>
                      </div>
                    )}
                    {impImgResult.error && <div style={{ marginTop: 6 }}>⚠ {impImgResult.error}</div>}
                  </div>
                )}
                {impImgResult?.error && impImgResult.amount_aed === undefined && (
                  <div className="ab-imp-result err">⚠ {impImgResult.error}</div>
                )}
              </div>

            </div>

            <div className="ab-section-title">Import History</div>
            <div className="ab-table-wrap">
              <table className="ab-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>File</th>
                    <th>Rows</th>
                    <th>Total AED</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {impHistory.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: "center", color: "#6d758b", padding: "24px 0" }}>
                        No imports yet. Drop a CSV, PDF, or receipt above.
                      </td>
                    </tr>
                  )}
                  {impHistory.map((h, i) => (
                    <tr key={h.id || i}>
                      <td>{h.date ? new Date(h.date).toLocaleString() : "—"}</td>
                      <td>{(h.type || "").toUpperCase()}{h.csv_kind ? ` · ${h.csv_kind}` : ""}</td>
                      <td>{h.filename || h.vendor || "—"}</td>
                      <td>{h.rows ?? 0}</td>
                      <td style={{ fontFamily: "monospace" }}>{fmt(h.total_aed ?? 0)}</td>
                      <td>
                        <span className={`ab-badge-status s-${h.status === "completed" ? "paid" : h.status === "failed" ? "overdue" : "partial"}`}>
                          {h.status || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── ONLINE P&L ──────────────────────────────────────────────────── */}
        {tab === "online-pl" && (
          <div>
            {loading['online-pl'] && <LoadingSpinner />}
            {error['online-pl'] && <ErrorMessage message={error['online-pl']} onRetry={loadOnlinePL} />}
            
            {!loading['online-pl'] && !error['online-pl'] && (
              <>
                <div className="ab-toolbar" style={{ alignItems: "center" }}>
                  <div className="ab-section-title" style={{ margin: 0 }}>Online P&L</div>
                  <input
                    className="ab-input"
                    type="month"
                    value={oplMonth}
                    onChange={e => setOplMonth(e.target.value)}
                    style={{ maxWidth: 180 }}
                    aria-label="Select month"
                  />
                  <button
                    className="ab-btn ab-btn-ghost"
                    disabled={loading['online-pl']}
                    onClick={async () => {
                      try {
                        const [y, m] = oplMonth.split("-").map(Number);
                        const from = `${oplMonth}-01`;
                        const last = new Date(y, m, 0).getDate();
                        const to = `${oplMonth}-${String(last).padStart(2, "0")}`;
                        const r = await (acctApi as any).syncWithExpenses(from, to);
                        alert(`Synced ${r.orders_synced} orders · Revenue AED ${r.revenue_total} · Expenses AED ${r.expenses_total} · Net AED ${r.net_profit}`);
                        loadOnlinePL();
                      } catch (e: any) { alert("Sync failed: " + (e?.message || e)); }
                    }}
                  >
                    🛍 Sync Shopify + Expenses
                  </button>
                  <button
                    className="ab-btn ab-btn-ghost"
                    onClick={async () => {
                      try {
                        const r = await (acctApi as any).seedSubscriptions();
                        alert(`Seeded ${r.seeded} subscription rows (${r.skipped} already existed).`);
                        loadOnlinePL();
                      } catch (e: any) { alert("Seed failed: " + (e?.message || e)); }
                    }}
                  >
                    💳 Seed Subscriptions
                  </button>
                  <button
                    className="ab-btn ab-btn-ghost"
                    disabled={loading['online-pl']}
                    onClick={loadOnlinePL}
                  >
                    {loading['online-pl'] ? "Loading…" : "↻ Refresh"}
                  </button>
                </div>

                {!oplData && (
                  <div style={{ color: "#6d758b", padding: "24px 0" }}>
                    Pick a month to see revenue, expenses and net profit.
                  </div>
                )}

                {oplData && !oplData.error && (
                  <>
                    <div className="ab-cards">
                      <StatCard
                        label={`Revenue (${oplData.revenue?.invoice_count ?? 0} invoices)`}
                        value={fmt(oplData.revenue?.gross ?? 0)}
                        sub={`Net ${fmt(oplData.revenue?.net ?? 0)} · VAT ${fmt(oplData.revenue?.vat ?? 0)}`}
                        variant="success"
                      />
                      <StatCard
                        label="Expenses (all categories)"
                        value={fmt(oplData.expenses?.total ?? 0)}
                        variant="danger"
                      />
                      <StatCard
                        label="Net Profit"
                        value={fmt(oplData.net_profit ?? 0)}
                        variant={(oplData.net_profit ?? 0) >= 0 ? "success" : "danger"}
                      />
                    </div>

                    <div className="ab-section-title">Expense Breakdown</div>
                    <div className="ab-table-wrap">
                      <table className="ab-table">
                        <thead>
                          <tr>
                            <th>Category</th>
                            <th style={{ textAlign: "right" }}>Amount (AED)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(oplData.expenses?.by_category ?? []).length === 0 && (
                            <tr>
                              <td colSpan={2} style={{ textAlign: "center", color: "#6d758b", padding: "24px 0" }}>
                                No expenses booked for this month. Try syncing Shopify or seeding subscriptions.
                              </td>
                            </tr>
                          )}
                          {(oplData.expenses?.by_category ?? []).map((row: any, i: number) => (
                            <tr key={i}>
                              <td>{row.category}</td>
                              <td style={{ textAlign: "right", fontFamily: "monospace" }}>{fmt(row.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {tab === "shopify-pl" && <ShopifyPLTab />}

      </div>{/* ab-content */}
    </div>
  );
}

// ── Shopify P&L Tab ─────────────────────────────────────────────────────────
function ShopifyPLTab() {
  const today = new Date();
  const monthStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState<string>(monthStr);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [status, setStatus] = useState<any>(null);
  const [costInputs, setCostInputs] = useState<Record<string, string>>({});

  const monthRange = (m: string) => {
    const [y, mo] = m.split("-").map(Number);
    const from = `${y}-${String(mo).padStart(2, "0")}-01`;
    const last = new Date(y, mo, 0).getDate();
    const to = `${y}-${String(mo).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
    return { from, to };
  };

  const loadStatus = useCallback(async () => {
    try { 
      setStatus(await (api.accounting as any).syncStatus()); 
    } catch (err) {
      console.error('Failed to load sync status:', err);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runSync = async () => {
    setLoading(true);
    setError(null);
    try {
      const { from, to } = monthRange(month);
      const res = await (api.accounting as any).fullSync(from, to);
      setResult(res);
      await loadStatus();
    } catch (e: any) {
      setError(e?.message || String(e));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const saveCost = async (title: string) => {
    const val = parseFloat(costInputs[title] || "0");
    if (!val || val <= 0) return;
    
    try {
      await (api.accounting as any).setProcurementCost({ product_title: title, cost_aed: val });
      setCostInputs((s) => ({ ...s, [title]: "" }));
      if (result?.unmatched_products) {
        setResult({
          ...result,
          unmatched_products: result.unmatched_products.filter((p: any) => p.title !== title),
        });
      }
    } catch (err: any) {
      alert(`Failed to save cost: ${err.message}`);
    }
  };

  const fmtN = (n: number) => (n ?? 0).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div>
      <h3 className="ab-section-title">Shopify → AfaqBooks P&L</h3>

      <div className="ab-toolbar">
        <label style={{ fontSize: ".8rem", color: "#8892b0" }}>Month</label>
        <input
          className="ab-input"
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          aria-label="Select month"
        />
        <button className="ab-btn ab-btn-primary" onClick={runSync} disabled={loading}>
          {loading ? "Syncing…" : "🔄 Sync from Shopify"}
        </button>
        {status?.last_sync_at && (
          <span style={{ fontSize: ".75rem", color: "#8892b0", marginLeft: "auto" }}>
            Last sync: {new Date(status.last_sync_at).toLocaleString()} · {status.orders_synced} orders total
          </span>
        )}
      </div>

      {error && <ErrorMessage message={error} onRetry={runSync} />}

      {result && !result.error && (
        <>
          <div className="ab-cards">
            <div className="ab-card">
              <div className="ab-card-label">Orders Synced</div>
              <div className="ab-card-value">{result.orders_synced ?? 0}</div>
              <div className="ab-card-sub">skipped: {result.orders_skipped ?? 0}</div>
            </div>
            <div className="ab-card success">
              <div className="ab-card-label">Revenue (gross)</div>
              <div className="ab-card-value">AED {fmtN(result.revenue)}</div>
              <div className="ab-card-sub">net: {fmtN(result.net)}</div>
            </div>
            <div className="ab-card">
              <div className="ab-card-label">VAT 5%</div>
              <div className="ab-card-value">AED {fmtN(result.vat)}</div>
            </div>
            <div className="ab-card danger">
              <div className="ab-card-label">COGS</div>
              <div className="ab-card-value">AED {fmtN(result.cogs)}</div>
            </div>
            <div className="ab-card danger">
              <div className="ab-card-label">Shipping</div>
              <div className="ab-card-value">AED {fmtN(result.shipping)}</div>
            </div>
            <div className={`ab-card ${result.profit >= 0 ? "success" : "danger"}`}>
              <div className="ab-card-label">Net Profit</div>
              <div className="ab-card-value">AED {fmtN(result.profit)}</div>
            </div>
          </div>

          {result.unmatched_products?.length > 0 && (
            <>
              <h4 className="ab-section-title">Unmatched Products — enter procurement cost</h4>
              <div className="ab-table-wrap">
                <table className="ab-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th style={{ textAlign: "right" }}>Qty sold</th>
                      <th style={{ width: 260 }}>Cost (AED)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.unmatched_products.map((p: any) => (
                      <tr key={p.title}>
                        <td>{p.title}</td>
                        <td style={{ textAlign: "right", fontFamily: "monospace" }}>{p.qty}</td>
                        <td>
                          <div style={{ display: "flex", gap: 8 }}>
                            <input
                              className="ab-input"
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={costInputs[p.title] ?? ""}
                              onChange={(e) => setCostInputs((s) => ({ ...s, [p.title]: e.target.value }))}
                              style={{ flex: 1 }}
                              aria-label={`Cost for ${p.title}`}
                            />
                            <button 
                              className="ab-btn ab-btn-primary ab-btn-sm" 
                              onClick={() => saveCost(p.title)}
                              disabled={!costInputs[p.title]}
                            >
                              Save
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {result.errors?.length > 0 && (
            <div style={{ marginTop: 14, fontSize: ".78rem", color: "#ff6b6b" }}>
              Errors: {result.errors.length}
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                {result.errors.slice(0, 5).map((e: string, i: number) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </>
      )}

      {result?.error && (
        <div style={{ color: "#ff6b6b", padding: 20 }}>Error: {result.error}</div>
      )}
    </div>
  );
}